import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, LogOut, Users, Link2, Package, Inbox, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import { SITE_LINKS } from "@/content/ContentContext";
import { PRODUCTS } from "@/data/content";
import { ProductIcon } from "@/lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "links", label: "Site Links", icon: Link2 },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "staff", label: "Staff", icon: Users },
];

const inputCls = "w-full bg-slate-800/70 border border-slate-700 focus:border-indigo-500 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors";

const ProductsTab = () => {
  const [draft, setDraft] = useState(() => {
    const d = {};
    PRODUCTS.forEach((p) => {
      d[p.slug] = { name: p.name, appUrl: p.appUrl || "", visible: true, tagline_en: p.tagline.en, tagline_bm: p.tagline.bm, desc_en: p.desc.en, desc_bm: p.desc.bm };
    });
    return d;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => {
      const o = data?.products || {};
      setDraft((prev) => {
        const next = { ...prev };
        Object.entries(o).forEach(([slug, f]) => {
          if (next[slug]) next[slug] = { ...next[slug], ...f, visible: f.visible !== false };
        });
        return next;
      });
    });
  }, []);

  const set = (slug, key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDraft((d) => ({ ...d, [slug]: { ...d[slug], [key]: v } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { products: draft, links: {} }, { withCredentials: true });
      toast.success("Products updated. Reloading site content...");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {PRODUCTS.map((p) => {
        const d = draft[p.slug];
        if (!d) return null;
        return (
          <div key={p.slug} data-testid={`admin-product-${p.slug}`} className="card-surface rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${p.accent.grad} flex items-center justify-center`}>
                <ProductIcon name={p.icon} className="w-4 h-4 text-white" />
              </span>
              <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-0.5 rounded-full ${p.accent.badge}`}>{p.short}</span>
              <label className="ml-auto flex items-center gap-2 text-xs text-slate-400">
                <input type="checkbox" data-testid={`visible-${p.slug}`} checked={d.visible} onChange={set(p.slug, "visible")} className="accent-indigo-500 w-4 h-4" />
                Visible on site
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid={`name-${p.slug}`} value={d.name} onChange={set(p.slug, "name")} placeholder="Product name" className={inputCls} />
              <input data-testid={`appurl-${p.slug}`} value={d.appUrl} onChange={set(p.slug, "appUrl")} placeholder="App URL (https://...) — links Open App button" className={inputCls} />
              <input value={d.tagline_en} onChange={set(p.slug, "tagline_en")} placeholder="Tagline (EN)" className={inputCls} />
              <input value={d.tagline_bm} onChange={set(p.slug, "tagline_bm")} placeholder="Tagline (BM)" className={inputCls} />
              <textarea value={d.desc_en} onChange={set(p.slug, "desc_en")} placeholder="Description (EN)" rows={2} className={`${inputCls} resize-none`} />
              <textarea value={d.desc_bm} onChange={set(p.slug, "desc_bm")} placeholder="Description (BM)" rows={2} className={`${inputCls} resize-none`} />
            </div>
          </div>
        );
      })}
      <button data-testid="save-products" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold px-7 py-3 text-sm transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Products
      </button>
    </div>
  );
};

const LinksTab = () => {
  const [links, setLinks] = useState({ ...SITE_LINKS });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => {
      if (data?.links) setLinks((l) => ({ ...l, ...data.links }));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { products: {}, links }, { withCredentials: true });
      toast.success("Site links updated. Reloading...");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface rounded-2xl p-6 space-y-4 max-w-xl">
      {[
        ["company", "Company name"], ["address", "Address"], ["phone", "Phone (display)"],
        ["whatsapp", "WhatsApp number (e.g. 60193906987)"], ["email", "Support email"],
      ].map(([key, label]) => (
        <div key={key}>
          <label className="text-xs text-slate-500 mb-1.5 block">{label}</label>
          <input data-testid={`link-${key}`} value={links[key] || ""} onChange={(e) => setLinks((l) => ({ ...l, [key]: e.target.value }))} className={inputCls} />
        </div>
      ))}
      <button data-testid="save-links" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold px-7 py-3 text-sm transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Links
      </button>
    </div>
  );
};

const LeadsTab = () => {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    axios.get(`${API}/leads`, { withCredentials: true }).then(({ data }) => setLeads(data.leads)).catch(() => setLeads([]));
  }, []);
  if (leads === null) return <Loader2 className="w-6 h-6 animate-spin text-slate-500" />;
  if (leads.length === 0) return <p className="text-sm text-slate-500">No leads yet.</p>;
  return (
    <div data-testid="admin-leads" className="space-y-3">
      {leads.map((l) => (
        <div key={l.id} className="card-surface rounded-2xl p-5">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <p className="font-semibold text-white text-sm">{l.full_name}</p>
            <span className="text-xs text-slate-400">{l.company}</span>
            <span className="ml-auto text-[10px] font-mono2 text-slate-500">{new Date(l.created_at).toLocaleString()}</span>
          </div>
          <p className="text-xs text-slate-400 mb-1">{l.email} • {l.phone} • {l.industry || "-"} • {l.employees || "-"}</p>
          {l.products?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 my-2">
              {l.products.map((p) => <span key={p} className="text-[10px] font-mono2 bg-indigo-500/15 text-indigo-300 rounded-full px-2 py-0.5">{p}</span>)}
            </div>
          )}
          {l.message && <p className="text-xs text-slate-500 mt-1">“{l.message}”</p>}
        </div>
      ))}
    </div>
  );
};

const StaffTab = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => axios.get(`${API}/auth/users`, { withCredentials: true }).then(({ data }) => setUsers(data.users)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/auth/users`, form, { withCredentials: true });
      toast.success(`User ${form.email} created`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return <p className="text-sm text-slate-500">Only admins can manage staff accounts.</p>;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form data-testid="staff-form" onSubmit={create} className="card-surface rounded-2xl p-6 space-y-4">
        <h3 className="font-heading font-semibold text-white">Create Staff Account</h3>
        <input data-testid="staff-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full name" className={inputCls} />
        <input data-testid="staff-email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className={inputCls} />
        <input data-testid="staff-password" required type="password" minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password (min 8 chars)" className={inputCls} />
        <select data-testid="staff-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={inputCls}>
          <option value="staff">Staff — can edit site content</option>
          <option value="admin">Admin — full access</option>
        </select>
        <button data-testid="staff-create" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold px-6 py-2.5 text-sm transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Create Account
        </button>
      </form>
      <div className="card-surface rounded-2xl p-6">
        <h3 className="font-heading font-semibold text-white mb-4">Accounts</h3>
        <div className="space-y-2.5">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-${u.email}`} className="flex items-center justify-between rounded-xl bg-slate-800/50 border border-slate-800 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-200">{u.name}</p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </div>
              <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full ${u.role === "admin" ? "bg-amber-500/15 text-amber-400" : "bg-slate-700/40 text-slate-400"}`}>{u.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("products");

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-indigo-400" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <div>
          <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-cyan-400 mb-1">OTOSUBS CMS</p>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-white">Website Admin</h1>
          <p className="text-xs text-slate-500 mt-1">{user.name} • {user.email} • {user.role}</p>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="ml-auto inline-flex items-center gap-2 text-sm text-slate-300 border border-slate-700 hover:border-slate-500 rounded-full px-5 py-2.5 transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 text-sm font-medium rounded-full px-5 py-2.5 transition-colors ${tab === id ? "bg-indigo-500 text-white" : "bg-slate-800/70 text-slate-400 hover:text-white"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab />}
      {tab === "links" && <LinksTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "staff" && <StaffTab />}
    </div>
  );
};

export default Admin;
